import {
  Box,
  Button,
  Flex,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { formatUnits } from '@ethersproject/units';
import Balance from '@ui/components/Balance/Balance';
import { Card } from '@ui/components/Card';
import Info from '@ui/components/Info/Info';
import { InfoTooltip } from '@ui/components/InfoTooltip';
import { InputBox } from '@ui/components/InputCard/InputCard';
import { InputCardStepper } from '@ui/components/InputCard/InputCardStepper';
import { RotatingBg } from '@ui/components/RotatingBg';
import { StrategyApySimpleLineChart } from '@ui/components/StrategyApySimpleLineChart';
import { useTransakModal } from '@ui/components/TransakModal';
import { pathDict } from '@ui/config/paths';
import { getPathInfo, useRealPathname } from '@ui/hooks/router';
import { useHistoricStrategyApys } from '@ui/hooks/useApr';
import { useStrategyDepositTransactionFee } from '@ui/hooks/useStrategyDepositTransactionFee';
import { useStrategyTokenUsdValue } from '@ui/hooks/useStrategyTokenUsdValue';
import { useTranslations } from '@ui/i18n';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import { formatPercentage, parseValue } from '@ui/utils/format';
import type {
  LiquidityStrategyDepositToken,
  LiquidityStrategyView,
} from '@ui/utils/strategy';
import {
  LiquidityStrategyTokenDict,
  LiquidityStrategyTokenIcon,
  useDepositTokenBalances,
  useDepositTokenBalanceUsd,
  useStrategyApy,
  useVaultBalance,
} from '@ui/utils/strategy';
import { pushRouterWithoutRerender } from '@ui/utils/urlHistory';
import { balanceValidator, decimalsValidator } from '@ui/utils/validators';
import { ArrowDown } from '@veno-app/icons';
import { expandDecimals } from '@veno-app/sdk';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

import DepositAndWithdrawConfirmButton from './DepositAndWithdrawConfirmButton';
import { ReceiveRows } from './ReceiveRows';
import StrategyApyInfo from './StrategyApyInfo';
import { TokenSelectionButton } from './TokenSelectionButton';
import { useReceiveTokenInfo } from './useReceiveTokenInfo';
import { useStrategyStore } from './useStrategyStore';

export const DepositAndWithdraw: FC = () => {
  const t = useTranslations();
  const pathInfo = getPathInfo(useRealPathname());

  return (
    <>
      <Tabs
        mb="16px"
        index={pathInfo.strategyView === 'withdraw' ? 1 : 0}
        onChange={(index) => {
          const strategyView: LiquidityStrategyView = index
            ? 'withdraw'
            : 'deposit';
          pushRouterWithoutRerender(pathDict.cro.strategy[strategyView]);

          // switch to default token (see PRD)
          useStrategyStore.getState().$$resetData?.();
        }}
        role="heading"
        aria-label="strategy vaults"
        aria-level={1}
      >
        <TabList>
          <Tab>{t('Deposit')}</Tab>
          <Tab>{t('Withdraw')}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <DepositContent />
          </TabPanel>
          <TabPanel>
            <WithdrawContent />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};

const DepositContent = () => {
  const t = useTranslations();
  const amountStr = useStrategyStore((s) => s.amountStr);
  const amountInputError = useStrategyStore((s) => s.amountInputError);
  const depositToken = useStrategyStore((s) => s.depositToken);
  const depositTransactionFee = useStrategyDepositTransactionFee(depositToken);
  const apyInfo = useStrategyApy().data;
  const openTransakModal = useTransakModal((s) => s.onOpen);
  const apyList = useHistoricStrategyApys();
  const [showChart, setShowChart] = useState(false);

  const tokenInfo = LiquidityStrategyTokenDict[depositToken];

  const balanceRet = useDepositTokenBalances()[depositToken];
  const balanceUsd = useDepositTokenBalanceUsd({
    token: depositToken,
    amount: balanceRet.data,
  });

  const amountUsd = useDepositTokenBalanceUsd({
    token: depositToken,
    amount: parseValue(
      amountStr,
      LiquidityStrategyTokenDict[depositToken].decimals,
    ),
  });

  const maxAmount = useMemo(() => {
    const balance = balanceRet.data;
    if (!depositTransactionFee || !balance) return '0';
    if (depositTransactionFee.lte(0))
      return formatUnits(balance, tokenInfo.decimals);
    return formatUnits(
      balanceRet.data.sub(depositTransactionFee),
      tokenInfo.decimals,
    );
  }, [balanceRet.data, depositTransactionFee, tokenInfo.decimals]);

  const onChange = (value: string) => {
    useStrategyStore.setState({
      amountStr: value,
      amountInputError:
        decimalsValidator(value) || balanceValidator(value, maxAmount),
    });
  };

  return (
    <>
      <Balance
        title={t('{coin} Wallet Balance', {
          coin: tokenInfo.name,
        })}
        icon={<tokenInfo.Icon width="32px" height="32px" />}
        amount={balanceRet.data}
        usdAmount={balanceUsd}
        usdDisplayDecimals={3}
        showUsdAmount
        mt="16px"
      />
      <Flex mt="4px" mb="36px" justify="center">
        <Button
          variant="link"
          color="text-link"
          onClick={() => {
            openTransakModal({
              entryPoint: 'Strategy',
            });
          }}
        >
          {t('Buy {coin}', {
            coin: 'CRO',
          })}
        </Button>
      </Flex>
      <Info
        title={
          <Flex align="center">
            {t('Liquidity strategy APY')}
            <InfoTooltip
              title={t('Liquidity strategy APY')}
              label={
                <StrategyApyInfo
                  strategyApy={`${(apyInfo.strategyApy * 100).toFixed(2)}%`}
                  immediateApy={`${(apyInfo.immediateApy * 100).toFixed(2)}%`}
                />
              }
              color="text-active"
              tooltipProps={{
                shadow: 'card.normal',
                minW: '407px',
              }}
            />
          </Flex>
        }
        value={
          <Flex gap={1} align="center">
            {formatPercentage(apyInfo.strategyApy)}{' '}
            <Icon
              cursor="pointer"
              as={ArrowDown}
              onClick={() => setShowChart((show) => !show)}
              transform={showChart ? 'rotate(180deg)' : undefined}
            />
          </Flex>
        }
        mt="30px"
      />
      {apyList && showChart && (
        <StrategyApySimpleLineChart apyList={apyList} mt="30px" />
      )}
      <Box mt="16px">
        <InputCardStepper
          max={maxAmount}
          maxDecimals={tokenInfo.maxDecimals}
          value={amountStr}
          onChange={onChange}
          mb="6px"
        />
        <Flex gap="8px">
          <InputBox
            flex="1"
            max={maxAmount}
            maxDecimals={tokenInfo.maxDecimals}
            label={t('Input {coin} amount', {
              coin: tokenInfo.name,
            })}
            value={amountStr}
            onChange={onChange}
            errorMessage={amountInputError}
            warningMessage={
              amountStr === String(maxAmount) && depositToken === 'cro'
                ? t('Gas fee has been deducted from maximum balance')
                : undefined
            }
            usdValue={amountUsd ? bigNumberToNumber(amountUsd) : undefined}
          />
          <TokenSelectionButton
            width="82px"
            value={depositToken}
            onCheck={(checkedToken) => {
              useStrategyStore.setState({
                checkedToken,
              });
            }}
            onChange={(depositToken) => {
              useStrategyStore.setState({
                depositToken,
                amountStr: '',
                amountInputError: '',
                checkedToken: depositToken,
              });
            }}
          />
        </Flex>
      </Box>
      <ReceiveRows
        mt={4}
        isWithdraw={false}
        token={depositToken}
        amountStr={amountStr}
        showDetails
      />
      <DepositAndWithdrawConfirmButton
        isWithdraw={false}
        amountStr={amountStr}
        amountInputError={amountInputError}
        token={depositToken}
        investAmountUsd={amountUsd ? bigNumberToNumber(amountUsd) : undefined}
      />
    </>
  );
};

const tokenList: LiquidityStrategyDepositToken[] = [
  'ferroLP',
  'cro',
  'wcro',
  'lcro',
];

const WithdrawContent = () => {
  const t = useTranslations();
  const withdrawStr = useStrategyStore((s) => s.withdrawStr);
  const withdrawInputError = useStrategyStore((s) => s.withdrawInputError);
  const withdrawToken = useStrategyStore((s) => s.withdrawToken);
  const withdrawBn = withdrawStr
    ? parseValue(
        withdrawStr,
        LiquidityStrategyTokenDict[withdrawToken].decimals,
      )
    : undefined;

  const { lowest, highest } = useReceiveTokenInfo(
    withdrawBn ?? expandDecimals(1, 18),
  );

  const vaultBalanceRet = useVaultBalance();
  const balanceUsd = useStrategyTokenUsdValue(vaultBalanceRet.data);
  const withdrawAmountUsd = useStrategyTokenUsdValue(withdrawBn);

  const tokenInfo = LiquidityStrategyTokenDict[withdrawToken];
  const maxAmount = vaultBalanceRet
    ? formatUnits(vaultBalanceRet.data ?? 0)
    : '0';

  const onChange = (value: string) => {
    useStrategyStore.setState({
      withdrawStr: value,
      withdrawInputError:
        decimalsValidator(value) || balanceValidator(value, maxAmount),
    });
  };

  return (
    <>
      <Balance
        title={t('{coin} deposit balance', {
          coin: tokenInfo.vaultTokenName,
        })}
        icon={<LiquidityStrategyTokenIcon width="32px" height="32px" />}
        amount={vaultBalanceRet.data}
        usdAmount={balanceUsd ?? undefined}
        showUsdAmount
        mt="16px"
      />
      <Box mt="32px">
        <InputCardStepper
          max={maxAmount}
          maxDecimals={tokenInfo.maxDecimals}
          value={withdrawStr}
          onChange={onChange}
          mb="6px"
        />
        <Flex gap="8px">
          <InputBox
            flex="1"
            max={maxAmount}
            maxDecimals={tokenInfo.maxDecimals}
            label={t('Input {coin} amount', {
              coin: tokenInfo.vaultTokenName,
            })}
            value={withdrawStr}
            onChange={onChange}
            errorMessage={withdrawInputError}
            usdValue={withdrawAmountUsd ?? undefined}
          />
        </Flex>
      </Box>
      <Flex mt={4} flexDir="column">
        <Text mb={4}>{t('Select token you wish to receive')}</Text>
        <Flex justify="space-between">
          {tokenList.map((token) => {
            const info = LiquidityStrategyTokenDict[token];
            const active = withdrawToken === token;
            return (
              <Flex
                cursor="pointer"
                align="center"
                w="80px"
                flexDir="column"
                key={info.name}
                onClick={() => {
                  useStrategyStore.setState({
                    withdrawToken: token,
                  });
                }}
              >
                <Card
                  rounded="full"
                  m={0}
                  shadow={active ? 'card.active' : 'card.normal'}
                >
                  {active ? (
                    <RotatingBg
                      w="50px"
                      h="50px"
                      justify="center"
                      alignItems="center"
                    >
                      <info.Icon width="32px" height="32px" />
                    </RotatingBg>
                  ) : (
                    <Flex justify="center" align="center" boxSize="50px">
                      <info.Icon width="32px" height="32px" />
                    </Flex>
                  )}
                </Card>
                <Text
                  my={1}
                  textStyle="captionBold"
                  fontWeight={active ? 'extrabold' : 'bold'}
                >
                  {info.name}
                </Text>
                {lowest === token && (
                  <Flex
                    border="1px solid"
                    px={2}
                    py={1}
                    borderColor="red.500"
                    color="red.500"
                    h="25px"
                    justify="center"
                    borderRadius="5px"
                    bg="white"
                    align="center"
                  >
                    <Text textStyle="ctaCopy">Lowest</Text>
                  </Flex>
                )}
                {highest === token && (
                  <Flex
                    border="1px solid"
                    color="green.500"
                    borderRadius="5px"
                    bg="white"
                    px={2}
                    py={1}
                    borderColor="green.500"
                    h="25px"
                    justify="center"
                    align="center"
                  >
                    <Text textStyle="ctaCopy">Best</Text>
                  </Flex>
                )}
                {lowest !== token && highest !== token && (
                  <Box w="full" py={1} h="25px" />
                )}
              </Flex>
            );
          })}
        </Flex>
      </Flex>
      <ReceiveRows
        isWithdraw
        token={withdrawToken}
        amountStr={withdrawStr}
        showDetails
        mt={6}
      />
      <DepositAndWithdrawConfirmButton
        isWithdraw={true}
        amountStr={withdrawStr}
        amountInputError={withdrawInputError}
        token={withdrawToken}
        investAmountUsd={
          withdrawAmountUsd !== null ? withdrawAmountUsd : undefined
        }
      />
    </>
  );
};
